import { Box, Stack, Typography } from "@mui/material";
import { ink, line, muted, panelBg } from "components/PimDashboard/DashboardComponents/shared/analyticsUi";

export const poColors = {
	ink,
	muted,
	line,
	panelBg,
	pageBg: "#fafaf8",
	white: "#ffffff",
	danger: "#b71c1c",
	dangerBg: "#ffebee",
	success: "#2e7d32",
	successBg: "#e8f5e9",
	warn: "#b76e00",
	warnBg: "#fff4e5",
	info: "#1565c0",
	infoBg: "#e3f2fd",
};

export const poStatusTone = {
	DRAFT: { color: poColors.ink, bgcolor: "#eef0f2" },
	PUBLISHED: { color: poColors.info, bgcolor: poColors.infoBg },
};

export const ginesysSyncTone = {
	PENDING: { color: poColors.warn, bgcolor: poColors.warnBg },
	DONE: { color: poColors.success, bgcolor: poColors.successBg },
	FAILED: { color: poColors.danger, bgcolor: poColors.dangerBg },
};

export const PoPage = ({ children, sx = {} }) => (
	<Box
		sx={{
			minHeight: "100%",
			bgcolor: poColors.pageBg,
			px: { xs: 1.5, md: 2.5 },
			py: 2,
			...sx,
		}}>
		{children}
	</Box>
);

export const PoPageHeader = ({ title, subtitle, actions, badge, compact = false }) => (
	<Box
		sx={{
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			justifyContent: "space-between",
			gap: compact ? 1 : 1.5,
			mb: compact ? 1.25 : 2,
			px: compact ? 1.75 : 2.5,
			py: compact ? 1 : 2,
			bgcolor: poColors.white,
			border: `1px solid ${poColors.line}`,
			borderRadius: compact ? "10px" : "14px",
			boxShadow: compact ? "none" : "0 8px 24px rgba(0,0,0,0.04)",
		}}>
		<Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" minWidth={0}>
			<Typography
				sx={{
					fontSize: compact ? { xs: 16, md: 18 } : { xs: 22, md: 26 },
					fontWeight: 700,
					color: poColors.ink,
					letterSpacing: "-0.02em",
					lineHeight: 1.2,
				}}>
				{title}
			</Typography>
			{badge}
			{subtitle != null && subtitle !== false && subtitle !== "" && (
				typeof subtitle === "string" || typeof subtitle === "number" ? (
					<Typography sx={{ fontSize: 12, color: poColors.muted, fontWeight: 500 }}>{subtitle}</Typography>
				) : (
					subtitle
				)
			)}
		</Stack>
		{actions && (
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
				{actions}
			</Stack>
		)}
	</Box>
);

export const PoPanel = ({ title, subtitle, actions, children, sx = {}, contentSx = {}, headerSx = {} }) => (
	<Box
		sx={{
			bgcolor: poColors.white,
			border: `1px solid ${poColors.line}`,
			borderRadius: "14px",
			boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
			overflow: "hidden",
			...sx,
		}}>
		{(title || actions) && (
			<Stack
				direction="row"
				alignItems="flex-start"
				justifyContent="space-between"
				spacing={1.25}
				flexWrap="wrap"
				useFlexGap
				sx={{
					px: 1.75,
					py: 1,
					borderBottom: `1px solid ${poColors.line}`,
					bgcolor: poColors.panelBg,
					...headerSx,
				}}>
				<Stack spacing={0.1} minWidth={0} sx={{ pt: actions ? 0.85 : 0 }}>
					{title && (
						<Typography sx={{ fontSize: 13, fontWeight: 700, color: poColors.ink, letterSpacing: "0.01em" }}>
							{title}
						</Typography>
					)}
					{subtitle && (
						<Typography sx={{ fontSize: 11.5, color: poColors.muted, fontWeight: 500 }}>{subtitle}</Typography>
					)}
				</Stack>
				{actions}
			</Stack>
		)}
		<Box sx={{ px: 1.75, py: 1.25, ...contentSx }}>{children}</Box>
	</Box>
);

export const PoStatusBadge = ({ status }) => {
	const tone = poStatusTone[status] || poStatusTone.DRAFT;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1.1,
				py: 0.35,
				borderRadius: "8px",
				fontSize: 12,
				fontWeight: 700,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				color: tone.color,
				bgcolor: tone.bgcolor,
				width: "fit-content",
			}}>
			{status || "DRAFT"}
		</Box>
	);
};

export const PoYesNoBadge = ({ value, yesLabel = "Yes", noLabel = "No" }) => {
	const on = !!value;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1.1,
				py: 0.35,
				borderRadius: "8px",
				fontSize: 12,
				fontWeight: 700,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				color: on ? poColors.success : poColors.muted,
				bgcolor: on ? poColors.successBg : "#eef0f2",
				width: "fit-content",
			}}>
			{on ? yesLabel : noLabel}
		</Box>
	);
};

export const PoGinesysSyncBadge = ({ value }) => {
	const key = value || "PENDING";
	const tone = ginesysSyncTone[key] || ginesysSyncTone.PENDING;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1.1,
				py: 0.35,
				borderRadius: "8px",
				fontSize: 12,
				fontWeight: 700,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				color: tone.color,
				bgcolor: tone.bgcolor,
				width: "fit-content",
			}}>
			{key}
		</Box>
	);
};

/** Shared General / Item Details control chrome (ENTER + SELECT). */
export const poFieldBg = "#fafafa"; // grey[50]
export const poFieldBgDisabled = "#e0e0e0"; // grey[300]

export const poFieldLabelSx = {
	display: "block",
	fontSize: 13,
	fontWeight: 600,
	color: poColors.muted,
	letterSpacing: "0.02em",
	lineHeight: 1.25,
	mb: 0.5,
};

export const poFieldControlSx = {
	minWidth: 0,
	width: "100%",
	// Kill wrapper fills from RenderInputField (grey bleeds past rounded borders).
	"& .MuiTextField-root, & .MuiAutocomplete-root, & .MuiFormControl-root, & .MuiStack-root": {
		background: "transparent !important",
		backgroundColor: "transparent !important",
	},
	"& .MuiInputLabel-root": { display: "none" },
	"& .MuiFormControl-root": { m: 0, width: "100%" },
	"& .MuiOutlinedInput-root": {
		minHeight: 40,
		height: 40,
		fontSize: 14,
		borderRadius: "8px",
		overflow: "hidden",
		backgroundColor: poFieldBg,
		"& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
		"&.Mui-disabled": {
			backgroundColor: poFieldBgDisabled,
		},
	},
	"& .MuiOutlinedInput-input": {
		py: "8px",
		px: 1.35,
		fontSize: 14,
		height: "auto",
		boxSizing: "border-box",
		backgroundColor: "transparent",
	},
	"& .MuiOutlinedInput-input.Mui-disabled": {
		WebkitTextFillColor: "rgba(0,0,0,0.55)",
		backgroundColor: "transparent",
	},
	"& .MuiAutocomplete-root": { width: "100%" },
	"& .MuiAutocomplete-root .MuiOutlinedInput-root": {
		height: 40,
		minHeight: 40,
		py: 0,
		paddingRight: "36px !important",
		overflow: "hidden",
		backgroundColor: poFieldBg,
		"&.Mui-disabled": {
			backgroundColor: poFieldBgDisabled,
		},
	},
	"& .MuiAutocomplete-input": { py: "6px !important", fontSize: 14, backgroundColor: "transparent" },
	"& .MuiOutlinedInput-root.MuiInputBase-multiline": {
		height: "auto",
		minHeight: 64,
		alignItems: "flex-start",
	},
	"& textarea": { fontSize: 14, py: "8px !important", minHeight: 56, backgroundColor: "transparent" },
};

/** Same chrome as poFieldControlSx, but keeps MUI floating outlined labels. */
export const poFieldControlFloatingSx = {
	...poFieldControlSx,
	"& .MuiInputLabel-root": {
		display: "block",
		fontSize: 14,
		color: poColors.muted,
		"&.Mui-focused": { color: poColors.ink },
		"&.MuiInputLabel-shrink": { fontSize: 12 },
	},
	"& .MuiOutlinedInput-root": {
		...poFieldControlSx["& .MuiOutlinedInput-root"],
		overflow: "visible",
	},
	"& .MuiAutocomplete-root .MuiOutlinedInput-root": {
		...poFieldControlSx["& .MuiAutocomplete-root .MuiOutlinedInput-root"],
		overflow: "visible",
	},
};

export const poPrimaryBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: "10px",
	px: 2.2,
	bgcolor: "#111110",
	"&:hover": { bgcolor: "#2a2a28" },
};

export const poSecondaryBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: "10px",
	px: 2.2,
	borderColor: "rgba(0,0,0,0.18)",
	color: poColors.ink,
	"&:hover": { borderColor: "rgba(0,0,0,0.35)", bgcolor: "rgba(0,0,0,0.03)" },
};

export const poGhostBtnSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: "10px",
	color: poColors.ink,
};
